<!-- eslint-disable vue/singleline-html-element-content-newline -->
<!-- eslint-disable vue/max-attributes-per-line -->
<!--
  新クロージングページ（eod-closing-v2）の経費管理ステップ用「諸入費」テーブル。

  UI（見た目・操作感）は eod-closing/components/ExpensesTable.vue を踏襲。
  データ・API は既存 daily_report/Closing/CashBenefits.vue と同じ v1
  （admin/Benefit, waiter/BenefitTag）を流用する。

  諸経費との違い:
  - Benefit はラベルが 2 階層（大ラベル / 小ラベル）のみ。
  - 税率・税額・非課税の概念が無いため、それらの列は持たない。

  保存方式: ExpensesTable と同様の「行単位保存」。
  - 新規行: 行末の「保存」ボタンで saveAll([row]) を呼ぶ。
  - 既存行: 金額・ラベル・現金区分の変更を debounce して saveAll([row]) で即保存。
  - 削除: deleteBenefits([id])。
  ※ create_all / update_all は createCount / updatedCount しか返さず、保存後の
    benefit データを返さないため、benefits.data を返す save_all を採用している。
-->
<template>
  <div class="benefits-table-wrapper">
    <v-skeleton-loader
      v-if="isMounting"
      type="table"
    />

    <v-data-table
      v-else
      :loading="isLoading"
      :headers="headers"
      :items="items"
      no-data-text="データがありません"
      loading-text="読込中..."
      :header-props="{ sortByText: 'ソート' }"
      hide-default-footer
      :items-per-page="-1"
      class="benefits-table"
    >
      <template #top>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>諸入費</v-toolbar-title>
          <v-spacer />
          <v-btn
            color="primary"
            :ripple="false"
            small
            @click="createBenefitRow"
          >
            <v-icon left small>{{ icons.mdiPlus }}</v-icon>
            追加
          </v-btn>
          <v-btn
            icon
            depressed
            :ripple="false"
            :loading="isLoading"
            class="ml-2 mr-1"
            @click="reloadBenefits"
          >
            <v-icon>{{ icons.mdiReload }}</v-icon>
          </v-btn>
          <v-divider
            vertical
            inset
            class="mx-2"
          />
          <v-menu
            bottom
            left
          >
            <template #activator="{ on: menuOn, attrs: menuAttrs }">
              <v-btn
                icon
                v-bind="menuAttrs"
                v-on="menuOn"
              >
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>

            <template #default>
              <v-list
                :disabled="isLoading"
                dense
              >
                <v-subheader>操作</v-subheader>
                <DataTableCopyListItemBtn
                  :disabled="isLoading"
                  :headers="headers"
                  :items="copyItems"
                />
              </v-list>
            </template>
          </v-menu>
        </v-toolbar>
      </template>

      <template
        v-for="obj in [
          {
            items: primaryLabels,
            key: 'benefitPrimaryLabelId',
            placeholder: '選択してください',
            required: true,
          },
          { items: secondaryLabels, key: 'benefitSecondaryLabelId' },
        ]"
        #[`item.${obj.key}`]="{ item }"
      >
        <v-combobox
          :key="`combobox-${obj.key}-${item.rowKey}`"
          :value="selectedLabelName(item, obj.key)"
          :items="obj.items"
          item-text="name"
          :placeholder="obj.placeholder"
          :clearable="!obj.required"
          :error="obj.required && item.triedSave && !item[obj.key]"
          dense
          hide-details
          :menu-props="{ auto: true, offsetY: true }"
          @change="onChangeLabel($event, item, obj.key)"
        />
      </template>

      <template #[`item.amount`]="{ item }">
        <v-text-field
          v-model.number="item.amount"
          type="number"
          inputmode="numeric"
          min="0"
          dense
          hide-details
          :error="item.triedSave && (typeof item.amount !== 'number' || item.amount === 0)"
          @change="onChangeAmount(item)"
        >
          <template #prepend-inner>¥</template>
        </v-text-field>
      </template>

      <template #[`item.withCashRegisterTransaction`]="{ item }">
        <v-checkbox
          v-model="item.withCashRegisterTransaction"
          hide-details
          dense
          style="width: 60px;"
          @change="onChangeCashFlag(item)"
        />
      </template>

      <template #[`item.actions`]="{ item }">
        <div style="display: flex; gap: 8px; align-items: center; padding-top: 4px; margin-top: 4px;">
          <v-btn
            v-if="isNew(item)"
            color="primary"
            :ripple="false"
            :disabled="!isValidBenefit(item)"
            small
            @click="createBenefit(item)"
          >
            保存
          </v-btn>

          <DeleteConfirmMenu
            v-if="!isNew(item)"
            @confirm="destroyBenefit(item)"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                text
                :ripple="false"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>{{ icons.mdiTrashCanOutline }}</v-icon>
              </v-btn>
            </template>
          </DeleteConfirmMenu>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  ref,
  toRefs,
  computed,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiReload,
  mdiPlus,
  mdiDotsVertical,
  mdiTrashCanOutline,
} from '@mdi/js'
import { debounce } from 'lodash'
import BenefitApi from '@/api/admin/Benefit'
import BenefitTagApi from '@/api/waiter/BenefitTag'
import useCompInit from '@/views/composable/useCompInit'
import useCurrentData from '@/views/composable/useCurrentData'
import useDateFormat from '@/views/composable/useDateFormat'
import useClubBusinessHours from '@/views/composable/useClubBusinessHours'
import DataTableCopyListItemBtn from '@/views/v2-temp/common/components/DataTableCopyListItemBtn.vue'
import DeleteConfirmMenu from '@/views/v2-temp/common/components/DeleteConfirmMenu.vue'

export default {
  name: 'BenefitsTable',
  components: {
    DataTableCopyListItemBtn,
    DeleteConfirmMenu,
  },
  props: {
    // 営業日（YYYY-MM-DD）
    date: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy
    const { date } = toRefs(props)
    const { isLoading, initWith } = useCompInit()
    const { currentClub } = useCurrentData()
    const { hhmm } = useDateFormat()
    const { businessHours } = useClubBusinessHours(date)

    // 保存用の benefit_on（営業日内の datetime）。
    //
    // benefits.benefit_on は datetime カラムで、index は
    // `where(benefit_on: club.beginning_of_day(date)..club.end_of_day(date))`
    // で絞り込む（営業日範囲 = その日の開店時刻〜翌日の開店時刻直前）。
    // date のみ（"2026-05-16"）を送ると 00:00:00 として保存され、開店時刻が
    // 0時より後の店舗では「前営業日」の範囲に入ってしまい、同じ date での
    // getBenefits で取得できず「リロードで消える」不具合になる。
    // CashBenefits の呼び出し元（IncomeTreeview 等）と同様に、営業日 +
    // 開店時刻（HH:mm）の datetime を benefit_on として送る。
    const benefitOnDateTime = computed(() => {
      // 営業日当時の開店時刻(サーバの営業時間履歴を時点解決)を優先。現在値のopenAtだと、
      // 過去営業日で当時の開店時刻が現在より遅い場合に営業日範囲の外へはみ出し「消える」不具合が再発する
      const openHhmm = businessHours.value?.open_at || hhmm(currentClub.value?.openAt) || '00:00'

      // "YYYY/MM/DD HH:mm"（new Date でパース可能・営業日範囲内の時刻）
      return `${String(date.value || '').replace(/-/g, '/')} ${openHhmm}`
    })

    const isMounting = ref(true)
    // API から取得した素の Benefit（{ id, attributes, meta }）
    const benefits = ref([])
    // ラベル候補（{ id, name } の配列）
    const primaryLabels = ref([])
    const secondaryLabels = ref([])

    const labelsRefByKey = {
      benefitPrimaryLabelId: primaryLabels,
      benefitSecondaryLabelId: secondaryLabels,
    }
    // createBenefitTag に渡す type。バックエンド（BenefitTagsController）が
    // params のキーとして benefit_primary_label / benefit_secondary_label を
    // 期待するため、camelCase で benefitPrimaryLabel / benefitSecondaryLabel を渡す。
    const tagTypeByKey = {
      benefitPrimaryLabelId: 'benefitPrimaryLabel',
      benefitSecondaryLabelId: 'benefitSecondaryLabel',
    }

    // ExpensesTable と揃えて actions 列を左端の固定列にする。
    // 列幅・class（fixed-cell）も ExpensesTable と同じにすることで、保存
    // ボタン・削除アイコンの位置が ExpensesTable と一致する。
    const headers = [
      {
        text: '',
        value: 'actions',
        sortable: false,
        maxWidth: '100px',
        minWidth: '100px',
        width: '100px',
        class: 'fixed-cell',
        cellClass: 'fixed-cell',
      },
      {
        text: '大ラベル', value: 'benefitPrimaryLabelId', sortable: false, width: '260px',
      },
      {
        text: '小ラベル', value: 'benefitSecondaryLabelId', sortable: false, width: '260px',
      },
      {
        text: '金額', value: 'amount', sortable: false, width: '200px',
      },
      {
        text: '現金', value: 'withCashRegisterTransaction', sortable: false, width: '100px',
      },
    ]

    const isNew = computed(() => item => !item.id)

    // v-data-table 表示用の行。benefits（＋未保存の新規行）から組み立てる。
    // 新規行は localNewRows に保持する。
    const localNewRows = ref([])

    const buildRowFromBenefit = benefit => {
      const attr = benefit.attributes || {}
      const meta = benefit.meta || {}

      return {
        id: benefit.id,
        rowKey: `benefit-${benefit.id}`,
        benefitPrimaryLabelId: attr.benefitPrimaryLabelId || null,
        benefitSecondaryLabelId: attr.benefitSecondaryLabelId || null,
        amount: attr.amount || 0,
        // 現金区分。サーバは meta.withCashRegisterTransaction で
        // 現金取引の有無を返す。
        withCashRegisterTransaction: !!meta.withCashRegisterTransaction,
        // 既存行は保存試行フラグは常に false（赤表示しない）。
        triedSave: false,
      }
    }

    // 保存済みを上、未保存（空・新規）を下に並べる。保存すると行は localNewRows
    // から benefits へ移るため、保存後に行が空行の下（最下部）へ飛ばず、保存済みと
    // して上位に表示される。
    const items = computed(() => [
      ...benefits.value.map(buildRowFromBenefit),
      ...localNewRows.value,
    ])

    // save_all は「保存した benefit のみ」を返すため、丸ごと置換すると既存の保存済み
    // 行が消える。id でマージ（upsert）して既存行を保持する。
    const mergeBenefits = (current, saved) => {
      const merged = [...current]
      saved.forEach(b => {
        const idx = merged.findIndex(x => Number(x.id) === Number(b.id))
        if (idx >= 0) merged.splice(idx, 1, b)
        else merged.push(b)
      })

      return merged
    }

    const selectedLabelName = computed(() => (item, key) => {
      const labelsRef = labelsRefByKey[key]
      if (!labelsRef) return ''
      const found = labelsRef.value.find(l => Number(l.id) === Number(item[key]))

      return found ? found.name : ''
    })

    const copyItems = computed(() => items.value.map(item => ({
      benefitPrimaryLabelId: selectedLabelName.value(item, 'benefitPrimaryLabelId') || '',
      benefitSecondaryLabelId: selectedLabelName.value(item, 'benefitSecondaryLabelId') || '',
      amount: item.amount,
      withCashRegisterTransaction: item.withCashRegisterTransaction ? '○' : '',
    })))

    const isValidBenefit = computed(() => item => (
      typeof item.amount === 'number' && item.amount !== 0 && !!item.benefitPrimaryLabelId
    ))

    // --- API: 取得 ------------------------------------------------------
    const getBenefits = async () => {
      isLoading.value = true
      try {
        const res = await BenefitApi.getBenefits({ date: date.value, interval: 0 })
        if (res?.data?.benefits?.data) {
          benefits.value = [...res.data.benefits.data]
        }
      } finally {
        isLoading.value = false
      }
    }

    const getBenefitTags = async () => {
      isLoading.value = true
      try {
        const res = await BenefitTagApi.getBenefitTags()
        if (res?.data?.benefitTags?.data?.attributes?.tags) {
          const { tags } = res.data.benefitTags.data.attributes
          primaryLabels.value = tags.primaryTags || []
          secondaryLabels.value = tags.secondaryTags || []
        }
      } finally {
        isLoading.value = false
      }
    }

    // --- 行追加・新規保存 ------------------------------------------------
    const buildEmptyRow = () => ({
      id: null,
      rowKey: `temp_${Date.now()}_${Math.floor(Math.random() * 1e9)}`,
      benefitPrimaryLabelId: null,
      benefitSecondaryLabelId: null,
      amount: 0,
      // 新規行の現金区分は true（現金）を初期値とする。
      withCashRegisterTransaction: true,
      // 「保存」を試行したかのフラグ。保存ボタン押下で true にし、
      // 必須項目（大ラベル・金額）未入力のセルを赤表示にする。
      // 行のフィールドが変更されたら false に戻し、編集で赤を解除する。
      triedSave: false,
    })

    const createBenefitRow = () => {
      localNewRows.value = [buildEmptyRow(), ...localNewRows.value]
    }

    // 行データを save_all 用の payload に変換する。
    // id 有り＝更新 / id 無し＝新規作成（save_all がどちらも処理する）。
    // benefitOn は date のみではなく営業日範囲内の datetime を送る
    // (理由は benefitOnDateTime のコメント参照)。
    const toBenefitPayload = item => {
      const payload = {
        benefitOn: benefitOnDateTime.value,
        amount: item.amount,
        benefitPrimaryLabelId: item.benefitPrimaryLabelId,
        benefitSecondaryLabelId: item.benefitSecondaryLabelId,
        withCashRegisterTransaction: !!item.withCashRegisterTransaction,
      }
      if (item.id) payload.id = item.id

      return payload
    }

    // 新規行を1件 API で作成する（ExpensesTable と同じ行単位保存）。
    // create_all は createCount しか返さないため、benefits.data を返す
    // save_all を使う。
    const createBenefit = async item => {
      // 「保存」を試行した瞬間にフラグを立てる。これ以降、必須項目が
      // 未入力なら赤表示になる（通常は disabled で押せないが、何らかの
      // 不具合で押されたときも入力を促せるようにする）。
      // eslint-disable-next-line no-param-reassign
      item.triedSave = true
      if (!isValidBenefit.value(item)) return

      try {
        isLoading.value = true
        const res = await BenefitApi.saveAll({ benefits: [toBenefitPayload(item)] })
        if (res?.data?.benefits?.data) {
          // 保存した新規行をローカルから除去し、サーバ結果を既存一覧へマージ。
          localNewRows.value = localNewRows.value.filter(r => r.rowKey !== item.rowKey)
          benefits.value = mergeBenefits(benefits.value, res.data.benefits.data)
        }
      } catch (error) {
        const messages = error.response?.data?.message
        if (Array.isArray(messages)) messages.forEach(msg => vm.$toast.error(msg))
        else if (messages) vm.$toast.error(messages)
      } finally {
        isLoading.value = false
      }
    }

    // --- 既存行の更新（debounce で即保存） ------------------------------
    // update_all は updatedCount しか返さないため、benefits.data を返す
    // save_all を使う。
    const updateBenefit = async item => {
      if (!item.id) return
      try {
        const res = await BenefitApi.saveAll({ benefits: [toBenefitPayload(item)] })
        if (res?.data?.benefits?.data) {
          benefits.value = mergeBenefits(benefits.value, res.data.benefits.data)
        }
      } catch (error) {
        const messages = error.response?.data?.message
        if (Array.isArray(messages)) messages.forEach(msg => vm.$toast.error(msg))
        else if (messages) vm.$toast.error(messages)
      }
    }
    const debounceUpdateBenefit = debounce(item => {
      updateBenefit(item)
    }, 500)

    // 編集が入ったら保存試行フラグを下ろし、必須未入力の赤表示を解除する。
    const clearTriedSave = item => {
      if (item.triedSave) {
        // eslint-disable-next-line no-param-reassign
        item.triedSave = false
      }
    }

    // 金額変更ハンドラ。新規行はローカル更新のみ、既存行は debounce 保存。
    const onChangeAmount = item => {
      clearTriedSave(item)
      if (item.id) debounceUpdateBenefit(item)
    }

    // 現金区分の変更ハンドラ。既存行は即保存（debounce）。
    const onChangeCashFlag = item => {
      clearTriedSave(item)
      if (item.id) debounceUpdateBenefit(item)
    }

    // --- ラベル変更 -----------------------------------------------------
    // 新規タグ作成（コンボボックスに文字列が入力されたとき）。
    const createNewLabel = async (key, name) => {
      const type = tagTypeByKey[key]
      if (!type) return null
      try {
        const res = await BenefitTagApi.createBenefitTag({ name, type })
        // 作成後はタグ一覧を取り直して候補へ反映する
        await getBenefitTags()
        const labelsRef = labelsRefByKey[key]
        const created = labelsRef.value.find(l => l.name === name)
        if (created) return created
        // レスポンスから直接拾えるならそれを使う
        const respTag = res?.data?.benefitTag?.data
        if (respTag) {
          return { id: respTag.id, name: respTag.attributes?.name || name }
        }
      } catch (e) {
        vm.$toast.error('ラベルの作成に失敗しました')
      }

      return null
    }

    const onChangeLabel = async (label, item, key) => {
      if (typeof label === 'string' && label !== '') {
        // 文字列入力 = 新規タグ作成
        const newLabel = await createNewLabel(key, label)
        // eslint-disable-next-line no-param-reassign
        item[key] = newLabel ? Number(newLabel.id) : null
      } else if (label && typeof label === 'object') {
        // eslint-disable-next-line no-param-reassign
        item[key] = Number(label.id)
      } else {
        // 選択解除
        // eslint-disable-next-line no-param-reassign
        item[key] = null
      }

      clearTriedSave(item)
      if (item.id) debounceUpdateBenefit(item)
    }

    // --- 削除 -----------------------------------------------------------
    const destroyBenefit = async item => {
      if (!item.id) {
        // 未保存の新規行はローカルから除くだけ
        localNewRows.value = localNewRows.value.filter(r => r.rowKey !== item.rowKey)

        return
      }
      try {
        isLoading.value = true
        const res = await BenefitApi.deleteBenefits([item.id])
        if (res?.data) {
          benefits.value = benefits.value.filter(b => Number(b.id) !== Number(item.id))
        }
      } catch (error) {
        const messages = error.response?.data?.message
        if (Array.isArray(messages)) messages.forEach(msg => vm.$toast.error(msg))
        else if (messages) vm.$toast.error(messages)
      } finally {
        isLoading.value = false
      }
    }

    // ExpensesTable と同じく、保存済み Benefit が 0 件のときは最初から
    // 入力可能な空行を一定数（10 行）並べる。データ取得後・リロード後・
    // マウント直後に呼ぶ。保存済みデータがある場合は空行を追加しない
    // (ユーザーは「追加」ボタンで明示的に増やす)。
    const INITIAL_EMPTY_ROW_COUNT = 10
    const setInitialBenefits = () => {
      if (benefits.value.length === 0 && localNewRows.value.length === 0) {
        localNewRows.value = [...Array(INITIAL_EMPTY_ROW_COUNT)].map(() => buildEmptyRow())
      }
    }

    const reloadBenefits = async () => {
      // リロード時は未保存の空行・新規行をクリアしてから取り直す
      // （空行が残り続ける不具合の解消）。保存済み 0 件のときのみ
      // setInitialBenefits で空行を再付与する。
      localNewRows.value = []
      await Promise.all([getBenefits(), getBenefitTags()])
      setInitialBenefits()
    }

    onMounted(async () => {
      await initWith([getBenefits(), getBenefitTags()])
      setInitialBenefits()
      isMounting.value = false
    })

    return {
      isMounting,
      isLoading,
      headers,
      items,
      primaryLabels,
      secondaryLabels,
      copyItems,
      isNew,
      isValidBenefit,
      selectedLabelName,

      createBenefitRow,
      createBenefit,
      onChangeAmount,
      onChangeCashFlag,
      onChangeLabel,
      destroyBenefit,
      reloadBenefits,

      icons: {
        mdiReload,
        mdiPlus,
        mdiDotsVertical,
        mdiTrashCanOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
// ルートにクラスを付け、それを起点にスコープ内で完結させる（グローバル漏れ防止）。
.benefits-table-wrapper {
  overflow-x: auto;
}

.benefits-table-wrapper ::v-deep .benefits-table {
  white-space: nowrap;
}

// ExpensesTable と同じく、actions 列（class: 'fixed-cell'）を左端に固定する。
// scoped + ::v-deep でテーブル配下のセルに反映させ、グローバルには漏らさない。
.benefits-table-wrapper ::v-deep .benefits-table {
  th.fixed-cell,
  td.fixed-cell {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 0;
    z-index: 3;
    background: var(--v-app-bar-base, #ffffff);
  }

  th.fixed-cell {
    z-index: 4;
  }
}

// ダークテーマでは固定列の背景色を Materio ダークの card 系に合わせる。
.theme--dark .benefits-table-wrapper ::v-deep .benefits-table {
  th.fixed-cell,
  td.fixed-cell {
    background: #312d4b;
  }
}
</style>
